import { inject, Injectable, Injector } from '@angular/core';
import { DB_CONSTANTS, replace } from '@wms/app/core/utils/utils';
import { getClonedObject, getFileObject, isEmptyObject, isFileObject, resetFileObjectsList, triggerFn } from '@wms/core';
import { each, extend, find, forEach, get, includes, isArray, isEmpty, isEqual, isNull, isObject, isString, keys, map, sortBy, split, trim, uniqueId } from 'lodash-es';
import { DatabaseService } from '../database-service/database.service';
import { ProjectService } from '../project/project.service';
import { I18nService, NotificationService, TypeFactoryService } from '@wms/services';
import { SWAGGER_CONSTANTS } from '@wavemaker/variables';
import { getValidJSON, isDefined, runtimeEventManager } from '@wm/core';

export let AppInjector: Injector;

@Injectable({
  providedIn: 'root'
})
export class DataModelDesignManagerService {

  operations: { [key: string]: any } = {};
  draftDataModel: any = {};
  publishedDataModel: any = {};
  activeQuery:any = {};
  activeProcedure:any = {};
  tableTypesMap:any = {
    "TABLE": "table",
    "VIEW": "view"
  };
  temporalTypes:any = {
    "APPLICATION": "BUSSINESS",
    "SYSTEM": "SYSTEM"
  };
  temporalColumnTypes:any = {
    "COLUMN_START": "PERIOD START",
    "COLUMN_END": "PERIOD END"
  };
  pubDM: any = {};
  connectionProperties: { [key: string]: any } = {};
  typesMap:any = {};
  generatorsMap:any = {};
  columnServerProperties:any = {};
  locale: any
  rowIndex =0
  colIndex = 0
  tablePosition :any= {};

  constructor(private injector: Injector, private projectService: ProjectService, private notificationService: NotificationService, private i18Service: I18nService, private databaseService: DatabaseService, private typeFactory : TypeFactoryService) {
    AppInjector = this.injector;
    this.locale = this.i18Service.getMessages();

  }

  /*Get the draft version for the specified dataModel.*/
  getDraftDataModel(projectID: any, dataModelName: any, success: any, error: any, reloadFlag?: boolean) {
    if (this.draftDataModel[dataModelName] && !reloadFlag) {
      triggerFn(success, this.draftDataModel[dataModelName]);
      return;
    }
    this.databaseService.getDataModel({
      projectID: projectID,
      dataModelName: dataModelName,
      isdraft: true
    }).then((response: any) => {
      this.draftDataModel[dataModelName] = response;
      triggerFn(success, response);
    }).catch((response: any) => {
      triggerFn(error, response);
    });
  }

  /*Get the published version for the specified dataModel.*/
  getPublishedDataModel(projectID: any, dataModelName: any, success: any, error: any, reloadFlag?: boolean) {
    if (this.publishedDataModel[dataModelName] && !reloadFlag) {
      triggerFn(success, this.publishedDataModel[dataModelName]);
      return;
    }

    let _pubDM = this.pubDM[dataModelName];

    if (!_pubDM) {
      _pubDM = this.pubDM[dataModelName] = [];
    }
    _pubDM.push({ success: success, error: error });

    if (!_pubDM.inProgress) {
      this.databaseService.getDataModel({
        projectID: projectID,
        dataModelName: dataModelName,
        isdraft: false
      }).then((response: any) => {
        this.publishedDataModel[dataModelName] = response;
        forEach(_pubDM, (obj) => {
          triggerFn(obj.success, response);
        });
        this.pubDM[dataModelName] = undefined;

      }).catch((response: any) => {
        forEach(_pubDM, (obj) => {
          triggerFn(obj.error, response);
        });
        this.pubDM[dataModelName] = undefined;
      });
    }
    _pubDM.inProgress = true;
  }

  /*Get the dataModel object.*/
  getDataModel(projectID: any, dataModelName: any, isDraft: any, success?: any, error?: any, reloadFlag?: boolean) {
    return new Promise((resolve, reject) => {
      if (isDraft) {
        this.getDraftDataModel(projectID, dataModelName, (response: any) => {
          triggerFn(success, response);
          resolve(response);
        }, (data: any) => {
          triggerFn(error, data);
          reject(data);
        }, reloadFlag);
      } else {
        this.getPublishedDataModel(projectID, dataModelName, (response: any) => {
          triggerFn(success, response);
          resolve(response);
        }, (data: any) => {
          triggerFn(error, data);
          reject(data);
        }, reloadFlag);
      }
    })
  }

  getEntityForTable(options: any, success: Function, error?: Function) {
    let projectId = options.projectId,
      dataModelName = options.dataModelName,
      tableName = options.tableName;

    this.getDataModel(projectId, dataModelName, false, (dataModel: any) => {
      const entities = dataModel.tables;
      triggerFn(success, find(entities, { name: tableName }));
    }, error);
  };

  prepareDbForStudio(dataModelName: any, isDraft: any) {
    return this.getDataModel(this.projectService.getProjectId(), dataModelName, isDraft, (publishedDataModel: any) => {
      return Promise.resolve(publishedDataModel);
    }, (error: any) => {
      /*If the published data-model does not exist, then it means the data-model is still in a draft state.
       * So get the draft version and add the nodes to the tree.
       * Else, display an error message.*/
      if (((isObject(error) && this.hasCodeProperty(error) && error.code.indexOf("$PublishedDataModelDNE") === -1)) || (isString(error) && error.indexOf("Published data model resource does not exist") === -1)) {
        this.notificationService.show("error", this.locale["MESSAGE_ERROR_TITLE"], this.locale["MESSAGE_ERROR_FETCH_DB_METADATA_DESC"]);
        return Promise.reject()
      } else {
        return this.getDataModel(this.projectService.getProjectId(), dataModelName, true, (draftDataModel: any) => {
          return Promise.resolve(draftDataModel);
        });
      }
    });
  }

  hasCodeProperty(obj: any): obj is { code: string } {
    return obj && typeof obj.code === 'string';
  }

  getAllQueriesForTree(projectID: any, dataModelName: any) {
    return new Promise((resolve, reject) => {
      this.databaseService.getAllQueries({
        projectID: projectID,
        dataModelName: dataModelName
      }).then((response: any) => {
        resolve(response);
      }).catch((error: any) => reject(error));
    })
  }

  getAllProceduresForTree(projectID: any, dataModelName: any) {
    return new Promise((resolve, reject) => {
      this.databaseService.getAllProcedures({
        projectID: projectID,
        dataModelName: dataModelName
      }).then((response: any) => {
        resolve(response);
      }).catch((error: any) => reject(error))
    })
  }

  /*Set the saved operations for the specified dataModel. Maintaining seperate objects to store queries and procedures.*/
  setOperations(dataModelName: string, dataModelOperations: any, type: any) {
    this.operations[dataModelName] = this.operations[dataModelName] || {};
    this.operations[dataModelName][type] = (this.operations[dataModelName][type] || []).concat(dataModelOperations);
  }

  /*Resetting the operations for each datamodel.*/
  resetOperations(dataModelName: string) {
    this.operations[dataModelName] = {};
  }

  /*Function to get database connection properties.*/
  getConnectionSettings(getDefault: any, projectID?: any, dataModelName?: string, successCallback?: any, errorCallback?: any) {
    /*Default connection properties object.*/
    if (getDefault) {
      return {
        'projectID': null,
        'serviceId': null,
        'packageName': null,
        'username': null,
        'password': null,
        'url': null,
        'dbType': null,
        'host': null,
        'dbName': null,
        'port': null,
        'tableFilter': [],
        'schemaName': null,
        'schemaFilter': "",
        'revengNamingStrategyClassName': null,
        'impersonateUser': null,
        'activeDirectoryDomain': null,
        'dialect': null,
        'metadataDialect': null,
        'overwrite': null,
        'driverClass': null,
        'readOnly': null
      };
    }

    if (this.connectionProperties[dataModelName!] && !isEmptyObject(this.connectionProperties[dataModelName!])) {
      if (!successCallback) {
        return getClonedObject(this.connectionProperties[dataModelName!]);
      }
      triggerFn(successCallback, this.connectionProperties[dataModelName!]);
      return;
    }
    this.databaseService.getConnectionProperties({
      'projectID': projectID,
      'dataModelName': dataModelName
    }).then((response) => {
      this.setConnectionProperties(dataModelName!, response);
      triggerFn(successCallback, response);
    }).catch((response) => {
      triggerFn(errorCallback, response);
    });
    return
  }

  /*Function to set connection propperties for a particular database.*/
  setConnectionProperties(dataModelName: string, connectionParams: any) {
    this.connectionProperties[dataModelName] = connectionParams;
  }

  /*Reset the dataModel object.*/
  resetDataModel(dataModelName: string) {
    this.draftDataModel[dataModelName] = undefined;
    this.publishedDataModel[dataModelName] = undefined;
  }

  /*Set the dataModel object.*/
  setDataModel(dataModelName: string, dataModel: any, isDraft: boolean) {
    if (isDraft) {
      this.draftDataModel[dataModelName] = dataModel;
    } else {
      this.publishedDataModel[dataModelName] = dataModel;
    }
  }

  setGeneratorValue(generatorValue: any, schema: any) {
    let generatorvalue = split(generatorValue, '.');
    return ((generatorvalue.length > 1) ? generatorValue : (schema + '.' + trim(generatorValue, '"')));
  }
    /*Set the activeQuery for the specified dataModel.*/
    setActiveQuery(dataModelName: string | number, query?: any) {
      this.activeQuery[dataModelName] = query;
    }

     /*Fetch the activeQuery for the specified dataModel.*/
     getActiveQuery(dataModelName: string) {
      return this.activeQuery[dataModelName] || undefined;
    }

    getActiveProcedure(dataModelName: string) {
      return this.activeProcedure[dataModelName] || undefined;
    }

     /*Iterate through the tables and construct the query completion list with table/entity and column/field names based on
         * whether the query type is nativeSql or HQL.*/
     getCompletionList(publishedDataModel:any, isNativeSql:any) {
      let completionList:any = [], score = 100000, columns,
          tables = sortBy(publishedDataModel.tables, (table) => {
              return table.name;
          });
      tables.forEach(function (dataTable) {
          let tableName = isNativeSql ? dataTable.name : dataTable.entityName,
              metaName = isNativeSql ? 'table' : 'entity';
          completionList.push({
              'name': tableName,
              'value': tableName,
              'meta': metaName,
              'score': score--
          });
          columns = sortBy(dataTable.columns, function (table) {
              return table.name;
          });

          columns.forEach(function (dataColumn) {
              let columnName = isNativeSql ? dataColumn.name : dataColumn.fieldName,
                  columnType = dataColumn.sqlType;

              metaName = isNativeSql ? 'column' : 'field';
              completionList.push({
                  'name': columnName,
                  'value': columnName,
                  'meta': metaName,
                  'type': columnType,
                  'score': score--
              });
          });
      });
      return completionList;
  }

     /*Set the parameters of the query/Procedure*/
     setParams(parameters:any, context:any) {
      return map(parameters, (parameter :any) => {
          let type = parameter.type, paramObject;
          if (parameter.variableType === 'SERVER') {
              if (parameter.variableName === "DATE_TIME") {
                  type = type.toUpperCase();
              } else {
                  type = parameter.variableName;
              }
          } else if (parameter.variableType === 'APP_ENVIRONMENT') {
              type = '__APP_ENV__' + parameter.variableName;
          }
          paramObject = {
              'type': type,
              'name': parameter.name,
              'testValue': parameter.testValue,
              'list': parameter.list,
              'required': parameter.required,
              'variableType': parameter.variableType,
              'variableName': parameter.variableName
          };
          if (context === 'query') {
              return paramObject;
          }
          if (context === 'procedure') {
              return extend({}, paramObject, { parameterType: parameter.parameterType});
          }
          return
      });
  }

     //prepare column server properties.
     prepareServerPropertiesForColumn(typesMap:any, dataModelName:any) {
      let typesMapKeys = keys(typesMap);
      this.columnServerProperties[dataModelName] = {};
      if (includes(typesMapKeys, 'date')) {
          this.columnServerProperties[dataModelName]['DATE'] = ['date'];
      }
      if (includes(typesMapKeys, 'time')) {
          this.columnServerProperties[dataModelName]['TIME'] = ['time'];
      }
      if (includes(typesMapKeys, 'datetime')) {
          this.columnServerProperties[dataModelName]['DATE_TIME'] = ['datetime'];
      }
      if (includes(typesMapKeys, 'timestamp')) {
          this.columnServerProperties[dataModelName]['TIMESTAMP'] = ['timestamp'];
      }
      if (includes(typesMapKeys, 'string')) {
          this.columnServerProperties[dataModelName]['STRING'] = ['string'];
      }
      if (includes(typesMapKeys, 'number')) {
          this.columnServerProperties[dataModelName]['INTEGER'] = ['integer'];
          this.columnServerProperties[dataModelName]['SHORT'] = ['short'];
          this.columnServerProperties[dataModelName]['LONG'] = ['long'];
          this.columnServerProperties[dataModelName]['FLOAT'] = ['float'];
          this.columnServerProperties[dataModelName]['DOUBLE'] = ['double'];
          this.columnServerProperties[dataModelName]['BYTE'] = ['byte'];
      }
  }

    //Function to get the types map
    getTypesMap(dataModelName: any) {
      let projectId = this.projectService.getProjectId();
      return new Promise((resolve, reject) => {
        if (this.typesMap[dataModelName]) {
          resolve(this.typesMap[dataModelName]);
      } else {
          this.databaseService.getTypesMap({'projectID': projectId, 'dataModelName': dataModelName}).then((response:any) => {
              this.typesMap[dataModelName] = response && response.typesMap;
              this.generatorsMap[dataModelName] = response && response.generators;
              //prepare server properties based on typesmap
              this.prepareServerPropertiesForColumn(this.typesMap[dataModelName], dataModelName);
              resolve(this.typesMap[dataModelName]);
          });
      }
      })
     
    }

    getColumnDetails(result:any, isNative: any) {
      let columnDetails:any = [];

      if (isEmpty(result)) {
          return columnDetails;
      }
      let dataTypes = this.typeFactory.getTypes();
      result = ((isNative || result.length > 1) ? result : (result.typeRef ? dataTypes[result.typeRef].fields : result));
      forEach(result, (column, name) => {
          columnDetails.push({
              'name'   : isNative ? column.name : name,
              'type'   : (isNative || column.fieldType) ? column.fieldType.typeRef : column.type,
              'isList' : isNative ? false : (column.isList || (column.fieldType && column.fieldType.list))
          });
      });
      return columnDetails;
  }

  prepareData(paramObject:any) {
    let formData = new FormData();
    forEach(paramObject['parameters'], function (param: any) {
        if (param.type === 'blob') {
            // get the file object using the param name
            let fileObj = getFileObject(param.name);
            if (fileObj) {
                formData.append(param.name, fileObj);
            } else if (isFileObject(param.testValue)) {
                formData.append(param.name, param.testValue);
            } else {
                param.testValue = undefined;
                formData.append(param.name, param.testValue);
            }
            param = '';
        }
    });
    resetFileObjectsList();
    formData.append(SWAGGER_CONSTANTS.WM_DATA_JSON, new Blob([JSON.stringify(paramObject)], {
        type: 'application/json'
    }));
    return formData;
}
 
  /*Fetch the saved operations for the specified dataModel.*/
  getOperations(dataModelName:any, type:any) {
    return this.operations[dataModelName] && this.operations[dataModelName][type];
  }

   /*Set the activeProcedure for the specified dataModel.*/
   setActiveProcedure(dataModelName: string | number, procedure?:any) {
    this.activeProcedure[dataModelName] = procedure;
  }

   /*Create request data for table.*/
   createRequestDataForTable(table: { columns?: any; schema: any; vpk?: any; dbobjectname?: any; entityname?: any; catalog?: any; defaultfilter?: any; }, columns: any, primaryKeys: any, relations: any) {
    return {
        "name": table.dbobjectname,
        "entityName": table.entityname,
        "dynamicInsert": false,
        "dynamicUpdate": false,
        "refreshEntity": false,
        "schema": table.schema,
        "catalog": table.catalog || "",
        "filter": table.defaultfilter,
        "columns": columns,
        "primaryKey": primaryKeys,
        "relations": relations,
        "constraints": []
    };
}
  /*Create request data for column.*/
  createRequestDataForColumn(column:any, constraintMap:any) {
    /* For blob type, don't send default length as 1 if length is not present. */
    //let length = (column.sqltype !== 'blob') ? column.length || 1 : column.length,
    let requestData:any = {
            'name'           : column.dbobjectname,
            'fieldName'      : column.fieldname,
            'sqlType'        : column.sqltype,
            'javaType'       : column.javatype,
            'unique'         : column.unique,
            'length'         : column.length || 0,
            'precision'      : column.precision || 0,
            'scale'          : column.scale || 0,
            'hidden'         : column.ishidden,
            'nullable'       : ((column.ispk || column.ispartofpk) && !(column.virtualpk || column.$parent?.$parent?.vpk)) ? false : column.nullable,
            'constraintList' : (constraintMap[column.columnidentifier] && constraintMap[column.columnidentifier].constraintList && constraintMap[column.columnidentifier].constraintList.length) ? constraintMap[column.columnidentifier].constraintList : []
        };
    switch (column.valuetype) {
    case 'user-defined':
        requestData.columnValue = {
            'type'          : column.valuetype,
            'defaultValue'  : column.defaultvalue === 'null' ? null : column.defaultvalue
        };
        requestData.columnValue.insertable = column.userinsertable;
        requestData.columnValue.updatable  = column.userupdatable;
        break;
    case 'server-defined':
        requestData.columnValue = {
            'type'  : column.valuetype,
            'name'  : column.serverproperty
        };
        requestData.columnValue.insertable = column.serverinsertable;
        requestData.columnValue.updatable  = column.serverupdatable;
        break;
    case 'app-environment-defined':
        requestData.columnValue = {
            'type' : column.valuetype,
            'name'  : column.appenvproperty
        };
        requestData.columnValue.insertable = column.appenvinsertable;
        requestData.columnValue.updatable  = column.appenvupdatable;
        break;
    case 'database-defined':
        requestData.columnValue = {
            'type' : column.valuetype
        };
        break;
    }
    return requestData;
  }

   /*Create request data for unique keys.*/
   createRequestDataForUniqueKeys(uniqueKeys:any) {
    return {
        "columns": uniqueKeys[0].columns,
        "virtual": uniqueKeys[0].virtual,
        "type": "UNIQUE"
    };
  }

 /*Create request data for relation.*/
 createRequestDataForRelation(relation:any, columnsFilter:any) {
  let mappings:any = [],
      relatedCols = relation.relatedcolumns.split(';'),
      columns = [];
  forEach(relatedCols, function (rel) {
      columns = rel.split(',');
      mappings.push({'sourceColumn': columns[0], 'targetColumn': columns[1]});
  });
  return {
      "name": relation.dbobjectname,
      "targetTable": relation.targettable,
      "cardinality": relation.cardinality,
      "virtual": relation.virtual,
      "sourceTable": relation.sourcetable,
      "mappings": mappings,
      "cascadeEnabled": relation.cascadeenabled,
      "cascadeOptions": {cascadeRemove: relation.cascaderemove},
      "fetchOptions": {fetch: getValidJSON(String(relation.fetchparent)), columnsFilter: columnsFilter[relation.name].parentcolumnsfilter || []},
      "parentFetchOptions": {fetch: getValidJSON(relation.fetchchild), columnsFilter: columnsFilter[relation.name].childcolumnsfilter || []}
  };
  }

    /*Create request data for primary keys.*/
    createRequestDataForPrimaryKeys(primaryKey:any) {
      let columns:any = [],
          primarycolumn:any;
      forEach(keys(primaryKey),  (col, index) => {
          if (index === 0) {
              primarycolumn = primaryKey[col].dbobjectkey;
          }
          columns.push(primaryKey[col].dbobjectname);
      });
      return {
          "columns"  : columns,
          "virtual"  : primaryKey[primarycolumn] && primaryKey[primarycolumn].virtualpk,
          "generator": {
            generatorType: primaryKey[primarycolumn] && ((primaryKey[primarycolumn].virtualpk && 'assigned') || primaryKey[primarycolumn].generatortype),
            generatorValue: primaryKey[primarycolumn] &&  primaryKey[primarycolumn].generatorvalue,
            schemaName: primaryKey[primarycolumn] && primaryKey[primarycolumn].sequenceschema
          }
      };
  }
  

  /*Generate the data for the dataModel to be rendered on the canvas.*/
  generateMarkup(dataModel:any, defaultSchemaName:any, constraintsMap:any, relationColumnsFilter:any, selectedTableInfo:any) {
    const tables: any = {};
    this.rowIndex = 0;
    this.colIndex = 0;
    this.tablePosition ={}
    let dbComponent:any = {
      widgetId: 'wm-dbdom',
      name: 'wm-dbdom',
      dbObjectKey: dataModel.name,
      dbobjectname: dataModel.name,
      package: dataModel.packageName,
      tables: [],
      relations: []
    };
    tables[selectedTableInfo.name] = { relations: [], temporals: [] };
    const selectedTable =  this.prepareDBDesignValues(selectedTableInfo,defaultSchemaName,dataModel,constraintsMap, 0)
    dbComponent.tables.push(selectedTable);
    selectedTableInfo?.relations?.forEach((relation: any) => {
      tables[selectedTableInfo.name].relations.push(relation);
    });
    if(selectedTableInfo?.relations.length > 0){
      dataModel.tables.forEach((table: any, tindex:any) => {
        selectedTableInfo?.relations.forEach(async (relation:any) => {
        if(relation.targetTable === table.name) {
          const tableName = table.name;
          let isExist = dbComponent.tables.some((t: { dbobjectname: any }) => t.dbobjectname === tableName);
            if(!isExist){
              tables[tableName] = { relations: [], temporals: [] };
              const tableType = table.type ? this.tableTypesMap[table.type] : 'table';
              const isVirtualPK = table.primaryKey && table.primaryKey.virtual;
              let foreignKeyCount = table.columns.filter((item: { foreignKey: boolean; partOfForeignKey: boolean }) => item.foreignKey || item.partOfForeignKey).length;
              const tableComponent:any = {
                type: tableType,
                dbObjectKey: tableName,
                dbobjectname: tableName,
                entityName: table.entityName,
                primarykey: table.primaryKey.generator.generatorType,
                generatorvalue: table.primaryKey.generator.generatorValue,
                schema: table.schema ? table.schema : defaultSchemaName,
                catalog: table.catalog,
                temporals: JSON.stringify(table.temporals),
                temporalType: (table.temporals && table.temporals.length) ? (table.temporals.length === 1 ? (table.temporals[0].type + ' TEMPORAL') : 'BI-TEMPORAL') : '',
                vpk: isVirtualPK,
                defaultfilter: table.filter,
                columns: [],
                tablePosition: {},
                primaryKeyCount: table.primaryKey.columns.length,
                foreignKeyCount: foreignKeyCount || 0,
                widgetid:"wm-db-"+dataModel.name+"-table"+(Number(tindex)+2),
                widgettype:"wm-db-table"
              };
          
              table.columns.forEach((column: any, cIndex:any) => {
                let javaType;
                const isFk = column.foreignKey || column.partOfForeignKey;
                const isColVirtual = isVirtualPK && column.partOfPrimaryKey;
                const columnIdentifier = '__' + dataModel.name + '__' + uniqueId();
                let temporalColumnType = '';
          
                if (DB_CONSTANTS.DATABASE_DATA_TYPES[column.javaType]) {
                  javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[column.javaType].java_type;
              } else if (DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[column.javaType]) {
                  javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[column.javaType].java_type].java_type;
              }
          
                if (column.period) {
                  temporalColumnType = this.getTemporalColumnType(table.temporals, column.name);
                }
          
                if (column.generatorValue && table.schema) {
                  column.generatorValue = this.setGeneratorValue(column.generatorValue, table.schema);
                }
          
                const columnComponent:any = {
                  dbObjectKey: column.name,
                  tableName: tableName,
                  dbobjectname: column.name,
                  fieldName: column.fieldName,
                  sqlType: column.sqlType,
                  isPk: column.primaryKey,
                  isPartOfPk: column.partOfPrimaryKey,
                  isFk: isFk,
                  virtualpk: isColVirtual,
                  unique: column.unique,
                  partOfUniqueKey: column.partOfUniqueKey,
                  nullable: column.nullable,
                  ishidden: column.hidden,
                  length: column.length,
                  precision: column.precision,
                  scale: column.scale,
                  javaType: javaType,
                  generatortype: column.generatorType,
                  generatorValue: column.generatorValue,
                  columnIdentifier: columnIdentifier,
                  period: column.period,
                  temporalColumnType: temporalColumnType,
                  widgetid:"wm-db-"+dataModel.name+"-table"+(Number(tindex)+2)+"-column"+(Number(cIndex)+1),
                  widgettype:"wm-db-table-column"
                };
                constraintsMap[columnIdentifier] = {}
                constraintsMap[columnIdentifier] = { constraintList: column.constraintList || [] };
          
                this.addColumnValueAttributes(column, columnComponent);
                tableComponent.columns.push(columnComponent);
              });
              tableComponent.tablePosition = this.getTopLeftForTable(table)
              dbComponent.tables.push(tableComponent);
              table?.relations?.forEach((relation: any) => {
                tables[tableName].relations.push(relation);
              });
            }
        }
      });
    });
    let relationIndex = 0;
    forEach(tables,(table: any) => {
      table?.relations?.forEach((relation: any) => {
        if(relation.primary && (relation.sourceTable === selectedTableInfo.name || relation.targetTable === selectedTableInfo.name)){
          relationIndex = relationIndex + 1
          let relationComponent:any = this.createRelationComponent(relation, table.entityName, relationColumnsFilter,relationIndex);
          relationComponent['widgetid'] = "wm-db-"+dataModel.name+"-column-relation"+relationIndex
          dbComponent.relations.push(relationComponent);
        }
      });
    });
    }
    return dbComponent;
  }

  prepareDBDesignValues(selectedTableInfo:any,defaultSchemaName:any,dataModel:any,constraintsMap:any,tindex:number){
    const tableName = selectedTableInfo.name;
    const tableType = selectedTableInfo.type ? this.tableTypesMap[selectedTableInfo.type] : 'table';
    const isVirtualPK = selectedTableInfo.primaryKey && selectedTableInfo.primaryKey.virtual;
    let foreignKeyCount = selectedTableInfo.columns.filter((item: { foreignKey: boolean; partOfForeignKey: boolean }) => item.foreignKey || item.partOfForeignKey).length;

    const tableComponent:any = {
      type: tableType,
      dbObjectKey: tableName,
      dbobjectname: tableName,
      entityName: selectedTableInfo.entityName,
      primarykey: selectedTableInfo.primaryKey.generator.generatorType,
      generatorvalue: selectedTableInfo.primaryKey.generator.generatorValue,      schema: selectedTableInfo.schema ? selectedTableInfo.schema : defaultSchemaName,
      catalog: selectedTableInfo.catalog,
      temporals: JSON.stringify(selectedTableInfo.temporals),
      temporalType: (selectedTableInfo.temporals && selectedTableInfo.temporals.length) ? (selectedTableInfo.temporals.length === 1 ? (selectedTableInfo.temporals[0].type + ' TEMPORAL') : 'BI-TEMPORAL') : '',
      vpk: isVirtualPK,
      defaultfilter: selectedTableInfo.filter,
      columns: [],
      tablePosition: {},
      primaryKeyCount: selectedTableInfo.primaryKey.columns.length,
      foreignKeyCount: foreignKeyCount || 0,
      widgetid:"wm-db-"+dataModel.name+"-table"+(Number(tindex)+1),
      widgettype:"wm-db-table"
    };


    selectedTableInfo.columns.forEach((column: any, cIndex:any) => {
      let javaType;
      const isFk = column.foreignKey || column.partOfForeignKey;
      const isColVirtual = isVirtualPK && column.partOfPrimaryKey;
      const columnIdentifier = '__' + dataModel.name + '__' + uniqueId();
      let temporalColumnType = '';

      if (DB_CONSTANTS.DATABASE_DATA_TYPES[column.javaType]) {
        javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[column.javaType].java_type;
    } else if (DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[column.javaType]) {
        javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[column.javaType].java_type].java_type;
    }

      if (column.period) {
        temporalColumnType = this.getTemporalColumnType(selectedTableInfo.temporals, column.name);
      }

      if (column.generatorValue && selectedTableInfo.schema) {
        column.generatorValue = this.setGeneratorValue(column.generatorValue, selectedTableInfo.schema);
      }

      const columnComponent:any = {
        dbObjectKey: column.name,
        tableName: tableName,
        dbobjectname: column.name,
        fieldName: column.fieldName,
        sqlType: column.sqlType,
        isPk: column.primaryKey,
        isPartOfPk: column.partOfPrimaryKey,
        isFk: isFk,
        virtualpk: isColVirtual,
        unique: column.unique,
        partOfUniqueKey: column.partOfUniqueKey,
        nullable: column.nullable,
        ishidden: column.hidden,
        length: column.length,
        precision: column.precision,
        scale: column.scale,
        javaType: javaType,
        generatortype: column.generatorType,
        generatorValue: column.generatorValue,
        columnIdentifier: columnIdentifier,
        period: column.period,
        temporalColumnType: temporalColumnType,
        widgetid:"wm-db-"+dataModel.name+"-table"+(Number(tindex)+1)+"-column"+(Number(cIndex)+1),
        widgettype:"wm-db-table-column"
      };
      constraintsMap[columnIdentifier] = { constraintList: column.constraintList || [] };

      this.addColumnValueAttributes(column, columnComponent);
      tableComponent.columns.push(columnComponent);
    });
    tableComponent.tablePosition = this.getTopLeftForTable(selectedTableInfo)
    return tableComponent;

  }

  getTemporalColumnType(temporals: any[], columnName: string): string {
    if (temporals.length === 1) {
      if (temporals[0].periodStart === columnName) {
        return this.temporalTypes[temporals[0].type] + ' ' + this.temporalColumnTypes.COLUMN_START;
      } else {
        return this.temporalTypes[temporals[0].type] + ' ' + this.temporalColumnTypes.COLUMN_END;
      }
    } else {
      for (const temporal of temporals) {
        if (temporal.periodStart === columnName) {
          return this.temporalTypes[temporal.type] + ' ' + this.temporalColumnTypes.COLUMN_START;
        } else if (temporal.periodEnd === columnName) {
          return this.temporalTypes[temporal.type] + ' ' + this.temporalColumnTypes.COLUMN_END;
        }
      }
    }
    return '';
  }

  addColumnValueAttributes(column: any, columnComponent: any) {
    if (column.columnValue) {
      columnComponent.valuetype = column.columnValue.type;
      switch (column.columnValue.type) {
        case 'user-defined':
          columnComponent.defaultvalue = column.columnValue.defaultValue || '';
          columnComponent.userinsertable = column.columnValue.insertable;
          columnComponent.userupdatable = column.columnValue.updatable;
          break;
        case 'server-defined':
          columnComponent.serverproperty = column.columnValue.name;
          columnComponent.serverinsertable = column.columnValue.insertable;
          columnComponent.serverupdatable = column.columnValue.updatable;
          break;
        case 'app-environment-defined':
          columnComponent.appenvproperty = column.columnValue.name;
          columnComponent.appenvinsertable = column.columnValue.insertable;
          columnComponent.appenvupdatable = column.columnValue.updatable;
          break;
      }
    }
  }

  createRelationComponent(relation: any, tableName: string, relationColumnsFilter: any,relationIndex:any) {
    const relationComponent = {
      dbObjectKey: relation.name,
      datasourcetable: relation.sourceTable,
      sourcecolumns: relation.mappings.map((map: any) => map.sourceColumn).toString(),
      datatargettable: relation.targetTable,
      targetcolumns: relation.mappings.map((map: any) => map.targetColumn).toString(),
      dbobjectname: relation.name,
      sourceTable: relation.sourceTable,
      targetTable: relation.targetTable,
      entityName: tableName,
      relatedColumns: relation.mappings.map((map: any) => map.sourceColumn + "," + map.targetColumn).join(';'),
      cardinality: relation.cardinality,
      virtual: relation.virtual,
      fieldName: relation.fieldName,
      cascadeRemove: relation.cascadeOptions.cascadeRemove,
      fetchChild: String(relation.parentFetchOptions.fetch),
      fetchParent: String(relation.fetchOptions.fetch),
      cascadeEnabled: relation.cascadeEnabled,
      childColumnsFilter: JSON.stringify(relation.parentFetchOptions.columnsFilter),
      parentColumnsFilter: JSON.stringify(relation.fetchOptions.columnsFilter),
      widgettype : "wm-db-column-relation",
    };

    relationColumnsFilter[relation.name] = {
      parentcolumnsfilter: relation.fetchOptions.columnsFilter,
      childcolumnsfilter: relation.parentFetchOptions.columnsFilter
    };

    return relationComponent;
  }

  getTopLeftForTable(element:any) {
    let top = 24,
        left = 24,
        defaultTableWidth = 242,
        gap = 45,
        defaultColumnHeight = 29,
        prevTableName,
        prevElement,
        tableName,
        retValue,
        height = element.columns ? (element.columns.length + 3) * defaultColumnHeight : (element.children.length + 3) * defaultColumnHeight,
        canvasContainerWidth:any = $('.wm-file-container').width(),
        tableElementsPerRow = Math.floor(canvasContainerWidth / (defaultTableWidth + gap));

    if (this.rowIndex > 0) {
        if (this.colIndex < tableElementsPerRow) {
            prevTableName = 't-' + this.rowIndex + '-' + this.colIndex;
            prevElement = this.tablePosition[prevTableName];
            if (this.rowIndex > 1) {
                prevTableName = 't-' + (this.rowIndex - 1) + '-' + (this.colIndex + 1);
                prevElement = this.tablePosition[prevTableName];
                top = prevElement.top + prevElement.height + gap;
                left = prevElement.left;
            } else {
                top = prevElement.top;
                left = prevElement.left + defaultTableWidth + gap * 2;
            }
            this.colIndex += 1;
        } else {
            this.colIndex = 1;
            prevTableName = 't-' + this.rowIndex + '-' + this.colIndex;
            prevElement = this.tablePosition[prevTableName];
            top = prevElement.top + prevElement.height + gap;
            left = prevElement.left;
            this.rowIndex += 1;
        }
    } else {
        this.rowIndex = 1;
        this.colIndex = 1;
    }
    tableName = 't-' + this.rowIndex + '-' + this.colIndex;
    retValue = {
        "top": top,
        "left": left,
        "height": height,
        "rowIndex": this.rowIndex,
        "colIndex": this.colIndex
    };
    this.tablePosition[tableName] = retValue;

    return retValue;
}

 /*Check whether the parameter is a valid db object type or not.*/
 isValidDBObjectType(dbObjectType:any) {
  return dbObjectType === "TABLE" || dbObjectType === "COLUMN" || dbObjectType === "RELATION";
  }

   /*Fetch the database object type based on the widget type*/
   getDbObjectType(widgetType:any) {
    let objectType = "";
    each(DB_CONSTANTS.OBJECTS, (dbWidgetType:any, dbObjectType:any)  =>{
        if (dbWidgetType === widgetType) {
            objectType = dbObjectType;
            return false;
        }
        return
    });
    return objectType;
  }

    /*Check whether the specified relation is valid or not.*/
    isValidRelation(params:any) {
      let response:any = {
          "isValid": true,
          "virtual": false
      };

      /*Display an error if the column data-types do not match and return without creating the relations.*/
      if (params.column1.getAttribute('sqltype') !== params.column2.getAttribute('sqltype')) {
          response = {
              "isValid": false,
              "virtual": false,
              "error":  this.locale["MESSAGE_ERROR_RELATION_NONMATCHING_COLUMN_TYPES"]
          };
          return response;
      }

      switch (params.cardinality) {
      case "OneToOne":
          response = this.isValidOneToOneRelation(params);
          break;
      case "OneToMany":
          response = this.isValidOneToManyRelation(params);
          break;
      case "ManyToOne":
          response = this.isValidManyToOneRelation(params);
          break;
      default:
          break;
      }
      return response;
  }
    /*Check whether the specified relation is a valid one-to-one relation or not.*/
    isValidOneToOneRelation(params:any) {
      let response:any = {
          "isValid": true,
          "virtual": false
      };
      if (!params.column1.getAttribute('ispk') && !params.column1.getAttribute('unique')) {
          response = {
              "isValid": false,
              "virtual": false,
              "error": replace(this.locale["MESSAGE_ERROR_RELATION_NONKEY_COLUMN"], [params.column1.getAttribute('dbobjectname'), params.table1.getAttribute('dbobjectname')])
          };
          return response;
      }
      /*if the source column is virtual primary key, then relation is virtual.*/
      if (params.column1.getAttribute('ispk') && params.column1.getAttribute('virtualpk')) {
          response = {
              "isValid": true,
              "virtual": true
          };
      }
      if (!params.column2.getAttribute('ispk')) {
          response = {
              "isValid": false,
              "virtual": false,
              "error": replace(this.locale["MESSAGE_ERROR_RELATION_NONKEY_COLUMN"], [params.column2.getAttribute('dbobjectname'), params.table2.getAttribute('dbobjectname')])
          };
          return response;
      }
      /*if the destination column is virtual primary key, then relation is virtual.*/
      if (params.column2.getAttribute('virtualpk')) {
          response = {
              "isValid": true,
              "virtual": true
          };
          return response;
      }
      return response;
  }
/*Check whether the specified relation is a valid one-to-many relation or not.*/
  isValidOneToManyRelation(params:any) {
      let response:any = {
          "isValid": true
      };
      /*Display an error if a "OneToMany" relation is being created and column1.getAttribute('is not a primary key*/
      if (params.column1.getAttribute('ispk') ==='false') {
          response = {
              "isValid": false,
              "error": replace(this.locale["MESSAGE_ERROR_RELATION_NONKEY_COLUMN"], [params.column1.getAttribute('dbobjectname'), params.table1.getAttribute('dbobjectname')])
          };
          return response;
      }
      /*if the source column is virtual primary key, then relation is virtual.*/
      if (params.column1.getAttribute('ispk') === 'true' && params.column1.getAttribute('virtualpk') === 'true') {
          response = {
              "isValid": true,
              "virtual": true
          };
      }
      if (params.column2.getAttribute('ispk') === 'true') {
          response = {
              "isValid": false,
              "error": this.locale["MESSAGE_ERROR_RELATION_BOTH_KEY_COLUMNS"]
          };
          return response;
      }
      /*if the parent table is a view, then the relation should be virtual*/
      response.virtual = this.isVirtualRelation(response.virtual, params.table2.getAttribute('type'));
      return response;
  }
/*Check whether the specified relation is a valid many-to-one relation or not.*/
  isValidManyToOneRelation(params:any) {
      let response:any = {
          "isValid": true
      };
      /*Display an error if a "ManyToOne" relation is being created and column2.getAttribute('is not a primary key*/
      if (params.column2.getAttribute('ispk') === 'false') {
          response = {
              "isValid": false,
              "error": replace(this.locale["MESSAGE_ERROR_RELATION_NONKEY_COLUMN"], [params.column2.getAttribute('dbobjectname'), params.table2.getAttribute('dbobjectname')])
          };
          return response;
      }
      if (params.column2.getAttribute('virtualpk') === 'true') {
          response = {
              "isValid": true,
              "virtual": true
          };
      }
      if (params.column1.getAttribute('ispk') === 'true') {
          response = {
              "isValid": false,
              "error": this.locale["MESSAGE_ERROR_RELATION_BOTH_KEY_COLUMNS"]
          };
          return response;
      }
      /*if the parent table is a view, then the relation should be virtual*/
      response.virtual = this.isVirtualRelation(response.virtual, params.table1.getAttribute('type'));
      return response;
  }
    /*check if the table is a view, then the relation should be virtual*/
    isVirtualRelation(isVirtual:any, tableType:any) {
      return isVirtual || (tableType === "view");
    }

    getAllEntities(options:any, success:any, error:any) {
      let projectId = options.projectId,
          dataModelName = options.dataModelName;

      this.getDataModel(projectId, dataModelName, false,  (dataModel:any) => {
          triggerFn(success, dataModel.tables);
      }, error);
  }

   getGenerators(dataModelName:any,javaType: any) {
    let generators: { [key: string]: any } = {};
    forEach(keys(this.generatorsMap[dataModelName].generatorsMap), (key) => {
      if (includes(this.generatorsMap[dataModelName].generatorsMap[key], javaType)) {
        generators[key] = this.locale['LABEL_DATABASE_GENERATORS_' + key.toUpperCase()] || key;
      }
    });
    return generators;
  }

     //Function to get javatypes based on sqltype and appropriate values.
     getJavaTypes(type:any, value1:any, value2:any, dataModelName:any) {
      let sqlType = this.typesMap[dataModelName] ? this.typesMap[dataModelName][type] : [],
          types;
      if (type !== 'number' && type !== 'string') {
          return sqlType ? sqlType[0] : {};
      }
      if (type === 'string') {
          types = find(sqlType, function (type) {
              return value1 >= type.lengthRange.min && value1 <= type.lengthRange.max;
          }) || {};
      } else if (type === 'number') {
          types = find(sqlType, function (type) {
              if (value2 === 0) {
                  return value1 >= type.precisionRange.min && value1 <= type.precisionRange.max;
              }
              return value1 >= type.precisionRange.min && value1 <= type.precisionRange.max &&
                  value2 >= type.scaleRange.min && value2 <= type.scaleRange.max;
          }) || {};
          if (isEmptyObject(types)) {
              if (value2 > 0) {
                  types = find(sqlType, function (type) {
                      return type.javaType === 'big_decimal';
                  });
              } else {
                  types = find(sqlType, function (type) {
                      return type.javaType === 'big_integer';
                  });
              }
          }
      }
      return types;
  }

    // Function to get the defaultGenerator
    getDefaultGenerator (dataModelName:string, javaType:any) {
      let defaultGenerator = this.generatorsMap[dataModelName].defaultGenerator;
      return (includes(this.generatorsMap[dataModelName].generatorsMap[defaultGenerator], javaType)) ? defaultGenerator : 'assigned';
  }

   //get column server properties for the database.
   getColumnServerProperties(dataModelName: string) {
    return this.columnServerProperties[dataModelName] || {};
  }
}
